import { formatCurrency } from '@medical-counsellor/shared';

interface PrintableEstimateProps {
    estimate: {
        referenceNumber: string;
        patientName: string;
        patientId?: string | null;
        totalAmount: number;
        isCustom?: boolean;
        customProcedureName?: string | null;
        icuDays?: number | null;
        procedureConfig?: {
            procedure?: {
                name: string;
            };
        };
        location?: {
            name?: string;
            city?: string;
            hospital?: {
                name?: string;
                disclaimerText?: string | null;
            };
        };
        createdAt: string | Date;
        estimateDoctors?: Array<{
            doctor: {
                name: string;
                qualification?: string | null;
            };
            isPrimary: boolean;
        }>;
        lines?: any[];
    };
    topSpacing?: number;
    bottomSpacing?: number;
    detailed?: boolean;
}

export function PrintableEstimate({
    estimate,
    topSpacing = 100,
    bottomSpacing = 100,
    detailed = false
}: PrintableEstimateProps) {
    const procedureName = estimate.isCustom
        ? (estimate.customProcedureName || 'Custom Procedure')
        : estimate.procedureConfig?.procedure?.name;

    const disclaimer = estimate.location?.hospital?.disclaimerText ||
        'This is an estimate only. Actual charges may vary based on clinical requirements, complications, and duration of stay. This estimate does not constitute a guarantee of pricing.';

    const currentDate = new Date().toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    const hospitalName = estimate.location?.hospital?.name || 'Medical Facility';

    // Group lines by section for detailed view
    const sections = detailed && estimate.lines ? estimate.lines.reduce((acc: Record<string, any[]>, line: any) => {
        if (!acc[line.sectionName]) {
            acc[line.sectionName] = [];
        }
        acc[line.sectionName].push(line);
        return acc;
    }, {}) : {};

    return (
        <div className="printable-estimate">
            <table style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <td>
                            <div style={{ height: topSpacing }} />
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div className="printable-content">
                                {/* Letterhead */}
                                <div className="letterhead">
                                    <div className="letterhead-line"></div>
                                    <h1 className="document-title">
                                        {detailed ? 'DETAILED TREATMENT ESTIMATE' : 'TREATMENT COST ESTIMATE'}
                                    </h1>
                                    <div className="letterhead-line"></div>
                                </div>

                                {/* Reference and Date */}
                                <div className="meta-info">
                                    <div className="meta-row">
                                        <span className="meta-label">Reference No:</span>
                                        <span className="meta-value">{estimate.referenceNumber}</span>
                                    </div>
                                    <div className="meta-row">
                                        <span className="meta-label">Date:</span>
                                        <span className="meta-value">{currentDate}</span>
                                    </div>
                                </div>

                                {/* Letter Body */}
                                <div className="letter-body">
                                    {/* Salutation */}
                                    <div className="salutation">
                                        <p><strong>To,</strong></p>
                                        <p><strong>{estimate.patientName}</strong></p>
                                        {estimate.patientId && (
                                            <p>Patient ID: {estimate.patientId}</p>
                                        )}
                                    </div>

                                    {/* Subject */}
                                    <div className="subject">
                                        <p><strong>Subject: Estimated Cost for Medical Treatment</strong></p>
                                    </div>

                                    {/* Opening */}
                                    <div className="opening">
                                        <p>Dear Patient,</p>
                                        <p>
                                            This is an estimated cost for your proposed medical treatment based on standard procedures and anticipated requirements.
                                        </p>
                                    </div>

                                    {/* Treatment Details & Cost Summary */}
                                    <div className="details-section">
                                        <h2 className="section-heading">Treatment Details & Cost Summary</h2>
                                        <div className="details-table">
                                            <div className="detail-row">
                                                <div className="detail-label">Procedure Name:</div>
                                                <div className="detail-value">{procedureName}</div>
                                            </div>
                                            {estimate.location && (
                                                <div className="detail-row">
                                                    <div className="detail-label">Location:</div>
                                                    <div className="detail-value">
                                                        {estimate.location.name}
                                                        {estimate.location.city && `, ${estimate.location.city}`}
                                                    </div>
                                                </div>
                                            )}
                                            {estimate.estimateDoctors && estimate.estimateDoctors.length > 0 && (
                                                <div className="detail-row">
                                                    <div className="detail-label">Treating Doctor(s):</div>
                                                    <div className="detail-value">
                                                        {estimate.estimateDoctors
                                                            .sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1))
                                                            .map((ed) => (
                                                                <div key={ed.doctor.name}>
                                                                    {ed.doctor.name}
                                                                    {ed.doctor.qualification ? `, ${ed.doctor.qualification}` : ''}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                            {estimate.icuDays != null && estimate.icuDays > 0 && (
                                                <div className="detail-row">
                                                    <div className="detail-label">ICU Days:</div>
                                                    <div className="detail-value">{estimate.icuDays} day{estimate.icuDays > 1 ? 's' : ''}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Detailed Breakdown */}
                                        {detailed && (
                                            <div className="detailed-breakdown">
                                                {Object.entries(sections)
                                                    .filter(([, lines]) => (lines as any[]).some((line) => line.lineTotal > 0))
                                                    .map(([sectionName, lines]) => (
                                                    <div key={sectionName} className="breakdown-section">
                                                        <h3 className="breakdown-section-title">{sectionName}</h3>
                                                        <table className="breakdown-table">
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-left">Service / Item</th>
                                                                    <th className="text-center w-qty">Qty</th>
                                                                    <th className="text-right w-amt">Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(lines as any[]).filter((line) => line.lineTotal > 0).map((line, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>
                                                                            {line.itemName}
                                                                            {line.quantity > 1 && (
                                                                                <div className="sub-text">
                                                                                    ({formatCurrency(line.unitPrice)} × {line.quantity})
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">{line.quantity}</td>
                                                                        <td className="text-right">{formatCurrency(line.lineTotal)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="cost-section">
                                            <div className="cost-row">
                                                <div className="cost-label">Total Estimated Cost:</div>
                                                <div className="cost-value">{formatCurrency(estimate.totalAmount)}</div>
                                            </div>
                                            <div className="cost-note">
                                                (Rupees {convertToWords(estimate.totalAmount)} only)
                                            </div>
                                        </div>
                                    </div>

                                    {/* Important Information & Disclaimer */}
                                    <div className="disclaimer-box">
                                        <h3 className="disclaimer-heading">IMPORTANT INFORMATION & DISCLAIMER</h3>
                                        <ul className="info-list">
                                            <li>Actual charges may vary based on clinical condition, complications, duration of stay, and additional procedures required.</li>
                                            <li>This estimate is for reference and planning purposes only.</li>
                                            <li>Contact our billing department for any clarifications.</li>
                                        </ul>
                                    </div>

                                    {/* Blank Space for Hospital Use */}
                                    <div className="signature-space"></div>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>
                            <div style={{ height: bottomSpacing }} />
                        </td>
                    </tr>
                </tfoot>
            </table>

            <style>{`
                @media print {
                    @page {
                        margin: 0;
                        size: auto;
                    }

                    html, body {
                        background: white !important;
                        color: black !important;
                    }

                    body * {
                        visibility: hidden;
                    }

                    .printable-estimate,
                    .printable-estimate * {
                        visibility: visible;
                    }

                    .printable-estimate {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        max-width: none;
                        margin: 0;
                        padding: 0;
                        background: white !important;
                    }

                    .breakdown-section-title {
                        background: #f5f5f5 !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                }

                .printable-estimate {
                    max-width: 210mm; /* A4 width */
                    margin: 0 auto;
                    font-family: 'Times New Roman', Times, serif;
                    color: #000;
                    background: white;
                    line-height: 1.6;
                }

                .printable-content {
                    padding: 0 40px; /* Reduced vertical padding since margins handle it */
                }

                /* Letterhead */
                .letterhead {
                    text-align: center;
                    margin-bottom: 20px;
                }

                .letterhead-line {
                    height: 2px;
                    background: #000;
                    margin: 8px 0;
                }

                .document-title {
                    font-size: 18px;
                    font-weight: bold;
                    letter-spacing: 2px;
                    margin: 12px 0;
                    color: #000;
                    text-transform: uppercase;
                }

                /* Meta Information */
                .meta-info {
                    text-align: right;
                    margin-bottom: 20px;
                    font-size: 10pt;
                }

                .meta-row {
                    margin-bottom: 5px;
                }

                .meta-label {
                    font-weight: normal;
                    margin-right: 10px;
                }

                .meta-value {
                    font-weight: bold;
                }

                /* Letter Body */
                .letter-body {
                    font-size: 10.5pt;
                }

                .salutation {
                    margin-bottom: 15px;
                }

                .salutation p {
                    margin: 2px 0;
                    line-height: 1.3;
                }

                .subject {
                    margin: 18px 0;
                    text-align: center;
                    text-decoration: underline;
                }

                .subject p {
                    margin: 0;
                }

                .opening {
                    margin-bottom: 18px;
                }

                .opening p {
                    margin: 8px 0;
                    text-align: justify;
                }

                /* Details Section */
                .details-section {
                    margin: 18px 0;
                }

                .section-heading {
                    font-size: 11pt;
                    font-weight: bold;
                    margin-bottom: 10px;
                    padding-bottom: 4px;
                    border-bottom: 1px solid #000;
                }

                .details-table {
                    margin-left: 15px;
                    margin-bottom: 20px;
                }

                .detail-row {
                    display: flex;
                    margin-bottom: 6px;
                }

                .detail-label {
                    flex: 0 0 140px;
                    font-weight: normal;
                }

                .detail-value {
                    flex: 1;
                    font-weight: bold;
                }

                /* Detailed Breakdown Styles */
                .detailed-breakdown {
                    margin: 20px 0;
                }

                .breakdown-section {
                    margin-bottom: 15px;
                    page-break-inside: avoid;
                }

                .breakdown-section-title {
                    font-size: 10pt;
                    font-weight: bold;
                    margin-bottom: 5px;
                    text-transform: uppercase;
                    background: #f0f0f0;
                    padding: 2px 5px;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }

                .breakdown-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 9.5pt;
                }

                .breakdown-table th {
                    border-bottom: 1px solid #000;
                    padding: 4px;
                    font-weight: bold;
                }

                .breakdown-table td {
                    padding: 4px;
                    border-bottom: 1px solid #eee;
                    vertical-align: top;
                }

                .breakdown-table tr:last-child td {
                    border-bottom: none;
                }

                .text-left { text-align: left; }
                .text-center { text-align: center; }
                .text-right { text-align: right; }
                .w-qty { width: 50px; }
                .w-amt { width: 100px; }

                .sub-text {
                    font-size: 8pt;
                    color: #555;
                    font-style: italic;
                }

                .cost-section {
                    margin: 20px 0;
                    padding: 15px 0;
                    border-top: 2px solid #000;
                    border-bottom: 2px solid #000;
                }

                .cost-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 6px;
                }

                .cost-label {
                    font-size: 12pt;
                    font-weight: bold;
                }

                .cost-value {
                    font-size: 15pt;
                    font-weight: bold;
                }

                .cost-note {
                    font-size: 9pt;
                    font-style: italic;
                    text-align: right;
                    margin-top: 3px;
                }

                /* Important Information & Disclaimer */
                .disclaimer-box {
                    border: 1px solid #000;
                    padding: 12px;
                    margin: 18px 0;
                }

                .disclaimer-heading {
                    font-size: 10pt;
                    font-weight: bold;
                    margin: 0 0 8px 0;
                }

                .info-list {
                    margin: 0 0 0 15px;
                    padding-left: 15px;
                }

                .info-list li {
                    margin-bottom: 5px;
                    text-align: justify;
                    font-size: 9pt;
                }

                .info-list li:last-child {
                    margin-bottom: 0;
                }

                /* Blank Space for Hospital Use */
                .signature-space {
                    height: 80px;
                    margin-top: 30px;
                }
            `}</style>
        </div>
    );
}

// Helper function to convert number to words (simplified for Indian numbering)
function convertToWords(amount: number): string {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (amount === 0) return 'Zero';

    const crores = Math.floor(amount / 10000000);
    const lakhs = Math.floor((amount % 10000000) / 100000);
    const thousands = Math.floor((amount % 100000) / 1000);
    const hundreds = Math.floor((amount % 1000) / 100);
    const remainder = Math.floor(amount % 100);

    let words = '';

    if (crores > 0) {
        words += convertToWords(crores) + ' Crore ';
    }
    if (lakhs > 0) {
        words += convertToWords(lakhs) + ' Lakh ';
    }
    if (thousands > 0) {
        words += convertToWords(thousands) + ' Thousand ';
    }
    if (hundreds > 0) {
        words += ones[hundreds] + ' Hundred ';
    }
    if (remainder >= 20) {
        words += tens[Math.floor(remainder / 10)] + ' ';
        if (remainder % 10 > 0) {
            words += ones[remainder % 10];
        }
    } else if (remainder >= 10) {
        words += teens[remainder - 10];
    } else if (remainder > 0) {
        words += ones[remainder];
    }

    return words.trim();
}
